import { Component, Input, Output, EventEmitter, OnInit, ChangeDetectionStrategy, ViewChild, ElementRef } from '@angular/core';
import DoughnutLabel from 'chartjs-plugin-doughnutlabel-v3';
import { PieChartDataItem } from '../chart.models';

@Component({
    selector: 'topo-pie-chart',
    templateUrl: './pie-chart.component.html',
    styleUrls: ['./pie-chart.component.scss'],
    standalone: false
})
export class PieChartComponent implements OnInit {
  @Input() chartType: 'pie' | 'doughnut' = 'pie';
  @Input() width: string;
  @Input() height: string;
  @Input() responsive: boolean;
  @Input() showPercentage: boolean;
  @Input() showValues: boolean;
  private totalSum: number;

  public get pieChartPlugins(): any[] {
    return this.chartType === 'doughnut' ? [DoughnutLabel] : [];
  }

  get chartOptions(): any {
    const total = this.getDataTotal();
    return {
      responsive: true,
      animation: false,
      maintainAspectRatio: false,
      plugins: {
        tooltip: {
          callbacks: {
            label: context => {
              let label = context.label || '';

              if (label) {
                label += ': ';
                label += context.formattedValue;
                if (this.showPercentage) {
                  label += '%';
                }
                if (this.showPercentage && this.showValues) {
                  label += " (" + context.dataset?.counts[context.dataIndex] + ")";
                }
              }
              return label;
            }
          }
        },
        datalabels: {
          display: false
        },
        legend: {
          onClick: (e, legendItem, legend) => this.legendClickHandler(e, legendItem, legend),
          onHover: (e, legendItem, legend) => {
            e.native.target.style.cursor = 'pointer';
          },
          onLeave: (e, legendItem, legend) => {
            e.native.target.style.cursor = 'default';
          },
          display: this.showLegend,
          position: 'right',
          align: 'center',
          labels: {
            generateLabels: chart => {
              const datasets = chart.data.datasets;
              if (!datasets || !datasets.length) {
                return [];
              }
              const meta = chart.getDatasetMeta(0);
              return datasets[0].counts.map((data, i) => {
                let dataText = '';
                if (this.showPercentage && this.totalSum === 0) {
                  dataText = "100%";
                }
                else {
                  dataText = this.showPercentage ? Math.round((data * 100) / this.totalSum) + '%' : data;
                }
                if (this.showValues && this.showPercentage) {
                  dataText += " (" + data + ")";
                }
                return {
                  text: ` ${chart.data.labels[i]} - ${dataText}`,
                  fillStyle: datasets[0].backgroundColor
                    ? datasets[0].backgroundColor[i]
                    : meta.controller.getStyle(i, true).backgroundColor,
                  strokeStyle: datasets[0].backgroundColor
                    ? datasets[0].backgroundColor[i]
                    : meta.controller.getStyle(i, true).backgroundColor,
                  pointStyle: 'circle',
                  hidden: false,
                  padding: 4,
                  usePointStyle: true,
                  datasetIndex: i
                };
              });
            },
            boxWidth: 80,
            padding: 20,
            pointStyle: 'circle',
            usePointStyle: true
          }
        },

        htmlLegendPlugin: {
          display: this.showLegend,
          containerID: 'legend-container'
        },

        doughnutLabel: {
          labels: [
            {
              text: total,
              font: {
                size: 20,
                weight: 'bold'
              }
            }
          ]
        }
      }
    };
  }

  @Input() showLegend: boolean;

  @ViewChild('legendContainer') legendContainer: ElementRef;

  @Input() set data(value: PieChartDataItem[]) {
    this._data = value;
    this.totalSum = 0;

    if (value?.length > 0) {
      value.forEach(v => (this.totalSum += v.data[0].count));
      const data = {
        labels: value.map(v => v.label),
        datasets: [
          {
            data: value.map(v =>
              this.showPercentage && this.totalSum ? Math.round((v.data[0].count * 100) / this.totalSum).toString() : v.data[0].count.toString()
            ),
            counts: value.map(v => v.data[0].count),
            backgroundColor: value.some(v => v.data[0].color != null) ? value.map(v => v.data[0].color) : undefined
          }
        ]
      };

      this.chartData = data;

      this.getDataTotal();
    } else {
      this.chartData = {
        labels: [],
        datasets: []
      };
    }
  }

  @Output() select = new EventEmitter<{ item: PieChartDataItem; subValue: any }>();

  chartData: {
    labels: string[] | undefined[];
    datasets:
      | {
          data: string[];
          counts: number[];
          backgroundColor?: string[];
        }[]
      | undefined[];
  };

  private _data: PieChartDataItem[];

  private legendClickHandler(e, legendItem, legend) {
    const index = legendItem.datasetIndex;
    this.select.emit({ item: this._data[index], subValue: undefined });
  }

  private getDataTotal(): string {
    this.totalSum = 0;
    if (this.chartData?.datasets.length > 0) {
      this.chartData?.datasets[0].counts.forEach(a => (this.totalSum += a));
    }

    return this.totalSum == 0 ? '' : this.totalSum.toString();
  }

  chartClick(event: any): void {
    if (!!event.active[0]) {
      this.select.emit({ item: this._data[event.active[0].index], subValue: undefined });
    }
  }

  private getOrCreateLegendList(chart, id): any {
    if (!this.legendContainer) {
      return null;
    }
    const legendContainer = this.legendContainer.nativeElement;
    let listContainer = legendContainer.querySelector('ul');

    if (!listContainer) {
      listContainer = document.createElement('ul');
      listContainer.style.display = 'flex';
      listContainer.style.flexDirection = 'column';
      listContainer.style.flexFlow = 'column wrap';
      listContainer.style.alignItems = 'left';
      listContainer.style.justifyContent = 'center';
      listContainer.style.margin = '4px';
      listContainer.style.padding = '8px';
      listContainer.style.height = '200px';
      listContainer.style.width = `min(calc(${this.width} - ${this.height}*2),600px)`;
      listContainer.style.marginLeft = '-35%';
      listContainer.style.overflowX = 'auto';
      listContainer.style.overflowY = 'hidden';

      legendContainer.appendChild(listContainer);
    }

    return listContainer;
  }

  htmlLegendPlugin: any = {
    id: 'htmlLegend',
    afterUpdate: (chart, args, options) => {
      return;
      const ul = this.getOrCreateLegendList(chart, 'legend-container');
      if (!ul) {
        return;
      }
      // Remove old legend items
      while (ul.firstChild) {
        ul.firstChild.remove();
      }

      // Reuse the built-in legendItems generator
      const items = chart.options.plugins.legend.labels.generateLabels(chart);

      items.forEach(item => {
        const li = document.createElement('li');
        li.style.alignItems = 'center';
        li.style.cursor = 'pointer';
        li.style.display = 'flex';
        li.style.flexDirection = 'row';
        li.style.marginLeft = '10px';
        li.style.padding = '4px';

        li.onclick = () => {
          const { type } = chart.config;
          if (type === 'pie' || type === 'doughnut') {
            // Pie and doughnut charts only have a single dataset and visibility is per item
            chart.toggleDataVisibility(item.index);
          } else {
            chart.setDatasetVisibility(item.datasetIndex, !chart.isDatasetVisible(item.datasetIndex));
          }
          chart.update();
        };

        // Color box
        const boxSpan = document.createElement('span');
        boxSpan.style.background = item.fillStyle;
        boxSpan.style.borderColor = item.strokeStyle;
        boxSpan.style.borderWidth = item.lineWidth + 'px';
        boxSpan.style.borderRadius = '50%';
        boxSpan.style.display = 'inline-block';
        boxSpan.style.height = '20px';
        boxSpan.style.marginRight = '10px';
        boxSpan.style.width = '20px';

        // Text
        const textContainer = document.createElement('p');
        textContainer.style.color = item.fontColor;
        textContainer.style.margin = '0';
        textContainer.style.padding = '0';
        textContainer.style.display = 'flex';
        textContainer.style.flexGrow = '1';
        textContainer.style.flexShrink = '0';
        textContainer.style.flexDirection = 'row';
        textContainer.style.gap = '8px';
        textContainer.style.justifyContent = 'space-between';
        textContainer.style.textDecoration = item.hidden ? 'line-through' : '';

        textContainer.innerHTML = item.text;

        li.appendChild(boxSpan);
        li.appendChild(textContainer);
        ul.appendChild(li);
      });
    }
  };

  ngOnInit(): void {}
}
